import { useState, useCallback, useEffect } from "react";
import { NavigateFunction } from "react-router-dom";
import { Song, SongModel } from "../../../Entities/SongModel";
import { SongSavedInListInfo } from "../../../Entities/ChordListModel";
import { useSongsModel } from "../../../utils/useDb";
import { fetchSongSavedStatus } from "../../../utils/useChordLists";
import { useUser } from "../../../utils/useUser";

function isHebrewText(text: string): boolean {
  return /[\u0590-\u05FF]/.test(text);
}

interface UseSongLoaderProps {
  songId: number;
  navigate: NavigateFunction;
}

export function useSongLoader({ songId, navigate }: UseSongLoaderProps) {
  const [song, setSong] = useState<Song>(
    new SongModel({ id: songId, title: "Loading..." })
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isHebrew, setIsHebrew] = useState(false);
  const [savedListsInfo, setSavedListsInfo] = useState<SongSavedInListInfo[]>([]);

  const { getSong } = useSongsModel();
  const { user, isAuthenticated } = useUser();

  const refreshSavedStatus = useCallback(
    async (idToRefresh: number) => {
      try {
        const status = await fetchSongSavedStatus(
          idToRefresh,
          isAuthenticated && user ? user.id : null
        );
        setSavedListsInfo(status);
      } catch {
        // ignore
      }
    },
    [isAuthenticated, user]
  );

  const loadSong = useCallback(async () => {
    if (!songId) return navigate("/Explore");

    setIsLoading(true);
    try {
      const fetchedSong = await getSong(songId);
      if (!fetchedSong) return navigate("/Explore");

      const hebrewRows = fetchedSong.body.filter(
        (row) => row.text && isHebrewText(row.text)
      );
      const englishRows = fetchedSong.body.filter(
        (row) => row.text && !isHebrewText(row.text)
      );
      setIsHebrew(hebrewRows.length > englishRows.length);

      setSong(fetchedSong);
    } catch (e) {
      console.error("Failed to load song:", e);
      navigate("/Explore");
    } finally {
      setIsLoading(false);
    }
  }, [songId, getSong, navigate]);

  useEffect(() => {
    loadSong();
  }, [loadSong]);

  useEffect(() => {
    if (song?.id) {
      refreshSavedStatus(song.id);
    }
  }, [song?.id, refreshSavedStatus]);

  return {
    song,
    setSong,
    isLoading,
    isHebrew,
    savedListsInfo,
    refreshSavedStatus,
    loadSong,
  };
}
